import { Fragment, useEffect, useState, type ReactNode, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import { Modal, Badge, toast } from '@/components';
import { api } from '@/api/client';

interface Props {
  open: boolean;
  submissionId: string | null;
  onClose: () => void;
  /** ステータス更新成功時に親側で refetch するコールバック */
  onStatusChanged?: () => void;
}

interface Submission {
  id: string;
  form_id: string;
  status: string;
  data: Record<string, string> | string;
  created_at: string;
  property_name?: string | null;
  property_address?: string | null;
}

interface StatusUpdateResponse extends Submission {
  auto_contract?: {
    deal_id: string | null;
    reused: boolean;
    contract_ids: string[];
    deal_type: string | null;
  } | null;
}

const STATUS_LABEL: Record<string, string> = {
  received: '受付', reviewing: '審査中', approved: '承認', rejected: '却下',
};
const STATUS_VARIANT: Record<string, 'blue' | 'amber' | 'green' | 'red' | 'gray'> = {
  received: 'blue', reviewing: 'amber', approved: 'green', rejected: 'red',
};

const FIELD_LABEL: Record<string, string> = {
  name: '氏名', name_kana: 'フリガナ', birth_date: '生年月日',
  phone: '電話番号', email: 'メール', current_address: '現住所',
  company_name: '勤務先', company_address: '勤務先住所', company_phone: '勤務先電話',
  annual_income: '年収(万円)', years_employed: '勤続年数',
  guarantor_name: '保証人氏名', guarantor_address: '保証人住所',
  guarantor_phone: '保証人電話', guarantor_company: '保証人勤務先',
  move_in_date: '入居希望日', num_residents: '入居人数', notes: '備考',
};

export function ApplicationSubmissionModal({ open, submissionId, onClose, onStatusChanged }: Props) {
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!open || !submissionId) { setSubmission(null); return; }
    setLoading(true);
    api<Submission>(`/tenant/application-submissions/${submissionId}`)
      .then(s => setSubmission(s))
      .catch(e => toast((e as Error)?.message || '申込データの取得に失敗しました', 'error'))
      .finally(() => setLoading(false));
  }, [open, submissionId]);

  const handleStatus = async (status: 'reviewing' | 'approved' | 'rejected') => {
    if (!submission) return;
    if (status === 'rejected' && !confirm('この申込を却下しますか?')) return;
    setUpdating(true);
    try {
      const updated = await api<StatusUpdateResponse>(`/tenant/application-submissions/${submission.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      setSubmission(s => (s ? { ...s, status: updated.status } : s));
      if (status === 'approved' && updated.auto_contract?.deal_id) {
        const { reused, deal_id } = updated.auto_contract;
        toast(reused ? '承認しました(既存の契約取引に紐付け)' : '承認 + 契約書を自動作成しました', 'success');
        // 契約確認へのナビゲート
        setTimeout(() => {
          onClose();
          navigate(`/contracts?tab=deals&deal=${deal_id}`);
        }, 600);
      } else {
        toast(`ステータスを「${STATUS_LABEL[status] ?? status}」に更新しました`, 'success');
      }
      onStatusChanged?.();
    } catch (e) {
      toast((e as Error)?.message || '更新に失敗しました', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const data: Record<string, string> = submission
    ? (typeof submission.data === 'string' ? (() => { try { return JSON.parse(submission.data); } catch { return {}; } })() : submission.data)
    : {};
  const status = submission?.status ?? '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="📋 申込詳細"
      width={640}
      footer={
        submission ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button style={btnOutline} onClick={onClose} disabled={updating}>閉じる</button>
            <button
              style={btnOutline}
              onClick={() => { onClose(); navigate(`/contracts?tab=submissions&submission=${submission.id}`); }}
              disabled={updating}
              title="Excel 書面化・PDF 生成は Contracts 画面へ"
            >📊 書面化へ</button>
            {status === 'received' && (
              <button style={btnOutline} onClick={() => handleStatus('reviewing')} disabled={updating}>🔍 審査開始</button>
            )}
            {status !== 'rejected' && (
              <button style={{ ...btnOutline, color: '#dc2626', borderColor: '#fecaca' }} onClick={() => handleStatus('rejected')} disabled={updating}>❌ 却下</button>
            )}
            {status !== 'approved' && (
              <button style={btnPrimary} onClick={() => handleStatus('approved')} disabled={updating}>✅ 承認</button>
            )}
          </div>
        ) : undefined
      }
    >
      {loading ? (
        <div style={{ padding: 20, color: 'var(--ink3)', fontSize: 13, textAlign: 'center' }}>読込中...</div>
      ) : !submission ? (
        <div style={{ padding: 20, color: 'var(--ink4)', fontSize: 13, textAlign: 'center' }}>申込データが見つかりません</div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Badge variant={STATUS_VARIANT[status] ?? 'gray'}>{STATUS_LABEL[status] ?? status}</Badge>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
              提出日時: {new Date(submission.created_at).toLocaleString('ja-JP')}
            </span>
          </div>

          {submission.property_name && (
            <Section title="対象物件">
              <div style={{ fontSize: 13, fontWeight: 600 }}>{submission.property_name}</div>
              {submission.property_address && (
                <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2 }}>{submission.property_address}</div>
              )}
            </Section>
          )}

          <Section title="基本情報">
            <FieldList data={data} keys={['name', 'name_kana', 'birth_date', 'phone', 'email', 'current_address']} />
          </Section>

          <Section title="勤務先">
            <FieldList data={data} keys={['company_name', 'company_address', 'company_phone', 'annual_income', 'years_employed']} />
          </Section>

          <Section title="連帯保証人">
            <FieldList data={data} keys={['guarantor_name', 'guarantor_address', 'guarantor_phone', 'guarantor_company']} />
          </Section>

          <Section title="入居情報">
            <FieldList data={data} keys={['move_in_date', 'num_residents']} />
          </Section>

          {data.notes && (
            <Section title="備考">
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{data.notes}</div>
            </Section>
          )}
        </div>
      )}
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink2)', marginBottom: 6, borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

function FieldList({ data, keys }: { data: Record<string, string>; keys: string[] }) {
  const rows = keys.filter(k => data[k]);
  if (rows.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--ink4)' }}>—</div>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 4, columnGap: 8, fontSize: 13 }}>
      {rows.map(k => (
        <Fragment key={k}>
          <div style={{ color: 'var(--ink3)' }}>{FIELD_LABEL[k] ?? k}</div>
          <div>{data[k]}</div>
        </Fragment>
      ))}
    </div>
  );
}

// React を使わず直接 type import に切替え、未使用な globalThis 使用を回避

const btnOutline: CSSProperties = {
  padding: '6px 12px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: '#fff', cursor: 'pointer',
};
const btnPrimary: CSSProperties = {
  padding: '6px 12px', fontSize: 13, borderRadius: 6, border: 'none', background: 'var(--brand, #0ea5e9)', color: '#fff', cursor: 'pointer', fontWeight: 600,
};
